import { useTheme } from '../contexts/ThemeContext';

const DRAW_MODES = [
  { id: 'point', icon: '📍', label: 'Point', desc: 'Click to place points' },
  { id: 'line', icon: '╱', label: 'Line', desc: 'Click vertices, double-click to finish' },
  { id: 'polygon', icon: '⬡', label: 'Polygon', desc: 'Click vertices, double-click to close' },
  { id: 'rectangle', icon: '▭', label: 'Rectangle', desc: 'Click a corner, then the opposite one' },
  { id: 'circle', icon: '⊙', label: 'Circle', desc: 'Click center, then click edge' },
  { id: 'freehand', icon: '✍️', label: 'Freehand', desc: 'Click and drag to draw' },
  { id: 'select', icon: '✥', label: 'Edit', desc: 'Select features, drag vertices to reshape' },
];

/**
 * DrawingPanel — drawing tools that work via map click callbacks
 *
 * Props:
 *   activeDrawMode - current draw mode string
 *   onModeChange(mode) - set active drawing mode
 *   features - array of drawn GeoJSON features
 *   onClearAll() - clear all features
 *   onUndo() - undo last feature
 *   onExport() - export features as GeoJSON
 *   onClose() - close panel
 */
export default function DrawingPanel({ activeDrawMode, onModeChange, features = [], onClearAll, onUndo, onExport, onDbPolygonSelect, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const featureCounts = {
    points: features.filter(f => f.geometry?.type === 'Point').length,
    lines: features.filter(f => f.geometry?.type === 'LineString').length,
    polygons: features.filter(f => ['Polygon', 'MultiPolygon'].includes(f.geometry?.type)).length,
  };
  const totalFeatures = features.length;

  return (
    <div style={{ width: 220, background: C.bg2, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>✏️ Draw</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      {/* Mode buttons */}
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflow: 'auto' }}>
        {DRAW_MODES.map(mode => (
          <button key={mode.id} onClick={() => onModeChange(activeDrawMode === mode.id ? null : mode.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
              borderRadius: 6, border: `1px solid ${activeDrawMode === mode.id ? C.cyan + '44' : C.border}`,
              background: activeDrawMode === mode.id ? C.bg4 : C.bg3,
              cursor: 'pointer', width: '100%', textAlign: 'left',
            }}>
            <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{mode.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: activeDrawMode === mode.id ? C.cyan : C.text0 }}>{mode.label}</div>
              <div style={{ fontSize: 9, color: C.text3 }}>{mode.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Feature counts */}
      <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', marginBottom: 6 }}>Drawn Features</div>
        <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
          <span style={{ color: C.cyan }}>📍 {featureCounts.points}</span>
          <span style={{ color: C.amber }}>╱ {featureCounts.lines}</span>
          <span style={{ color: C.emerald }}>⬡ {featureCounts.polygons}</span>
        </div>
        <div style={{ fontSize: 10, color: C.text2, marginTop: 4 }}>Total: {totalFeatures}</div>
      </div>

      {/* Actions */}
      <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {onDbPolygonSelect && (
          <button onClick={onDbPolygonSelect} disabled={featureCounts.polygons === 0}
            title={featureCounts.polygons === 0 ? 'Draw a polygon first' : 'Query database features inside the last drawn polygon (PostGIS)'}
            style={{ width: '100%', padding: '7px', borderRadius: 6, border: `1px solid ${featureCounts.polygons > 0 ? 'rgba(52,211,153,0.3)' : C.border}`, background: featureCounts.polygons > 0 ? 'rgba(52,211,153,0.12)' : C.bg4, color: featureCounts.polygons > 0 ? C.emerald : C.text3, fontWeight: 600, fontSize: 11, cursor: featureCounts.polygons > 0 ? 'pointer' : 'default' }}>
            🎯 Select DB Features in Polygon
          </button>
        )}
        <button onClick={onExport} disabled={totalFeatures === 0}
          style={{ width: '100%', padding: '7px', borderRadius: 6, border: 'none', background: totalFeatures > 0 ? 'linear-gradient(135deg,#00b8de,#3b82f6)' : C.bg4, color: totalFeatures > 0 ? '#fff' : C.text3, fontWeight: 600, fontSize: 11, cursor: totalFeatures > 0 ? 'pointer' : 'default' }}>
          💾 Export GeoJSON
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onUndo} disabled={totalFeatures === 0}
            style={{ flex: 1, padding: '6px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg4, color: C.text2, fontSize: 10, cursor: totalFeatures > 0 ? 'pointer' : 'default' }}>
            ↩ Undo
          </button>
          <button onClick={onClearAll} disabled={totalFeatures === 0}
            style={{ flex: 1, padding: '6px', borderRadius: 6, border: `1px solid rgba(248,113,113,0.25)`, background: 'rgba(248,113,113,0.1)', color: C.red, fontSize: 10, cursor: totalFeatures > 0 ? 'pointer' : 'default' }}>
            🗑 Clear
          </button>
        </div>
      </div>
    </div>
  );
}
